import "../style/components-style/NetZeroBottomTool.css";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import React, { useEffect, useMemo } from "react";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material";
import AgGrid from "../components/grid/AgGrid";
import { leftAlignColDef, centerAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { ChartCard, M3Button, FormSection, FieldGrid, M3Field, DataTableCard, Chip, SegmentedButton } from "../components/m3";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Scope12ShortTermTool({
                                  resetForm,
                                  saveShortTermToolInputs,
                                  baseYear,
                                  setBaseYear,
                                  baseYearList,
                                  targetYear,
                                  setTargetYear,
                                  targetYearList,
                                  mostRecentYear,
                                  setMostRecentYear,
                                  mostRecentYearList,
                                  scope1Emission,
                                  scope2Emission,
                                  MRYScope1Emission,
                                  setMRYScope1Emission,
                                  MRYScope2Emission,
                                  setMRYScope2Emission,
                                  scope1TargetEmission,
                                  scope2TargetEmission,
                                  yearsList,
                                  shortTermGraphRef,
                                  shortTermGraphData,
                                  shortTermGraphOptions,
                                  yScaleType = "logarithmic",
                                  setYScaleType = () => {},
                              }) {
    const { t } = useTranslation();
    const {
            selectedProducts,
            setSelectedProducts,
            setSelectedProductIds,
            refresh,
            setRefresh,
            columnVisibilities,
            isSaveClicked,
            setIsSaveVisible,
            setIsLoading,
            setIsSaveClicked,
            envFactorOptions,
          } = useContextProvider();
    useEffect(() => {
      if (isSaveClicked) {
        saveShortTermToolInputs();
        setIsSaveClicked(false);
        setIsLoading(false);
        setIsSaveVisible(false);
      }
    }, [isSaveClicked]);
    const selectStyle = {
      width: "100%",
      height: "40px",
      borderRadius: "8px",
      padding: "0 12px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "13.5px",
      fontWeight: 500,
      color: "var(--m3-on-surface, #1b1c1a)",
      backgroundColor: "var(--m3-surface, #ffffff)",
      border: "1px solid var(--m3-outline-variant, #e5e7eb)",
      outline: "none",
      cursor: "pointer",
    };

    const acaRowData = useMemo(() => {
      const s1Base = Number(scope1Emission) || 0;
      const s2Base = Number(scope2Emission) || 0;
      const s1MRY = Number(MRYScope1Emission) || 0;
      const s2MRY = Number(MRYScope2Emission) || 0;
      const s1Target = Number(scope1TargetEmission) || 0;
      const s2Target = Number(scope2TargetEmission) || 0;

      const s1Red = s1Base >= 0.01 ? ((1 - (s1MRY / s1Base)) * 100).toFixed(2) : "N/A";
      const s2Red = s2Base >= 0.01 ? ((1 - (s2MRY / s2Base)) * 100).toFixed(2) : "N/A";

      const totalBase = s1Base + s2Base;
      const totalMRY = s1MRY + s2MRY;
      const totalTarget = s1Target + s2Target;
      const totalRed = totalBase >= 0.01 ? ((1 - (totalMRY / totalBase)) * 100).toFixed(2) : "N/A";

      const sbtRedVal = yearsList.length > 1 ? (((yearsList.length - 1) * 0.042) * 100).toFixed(2) : "0.00";

      return [
        {
          id: "scope1",
          metric: t("Scope 1 Emissions (t CO₂e)"),
          baseYear: s1Base.toFixed(2),
          mry: s1MRY.toFixed(2),
          targetYear: s1Target.toFixed(2),
          reductionToDate: s1Red,
          sbtReduction: `${sbtRedVal}%`,
        },
        {
          id: "scope2",
          metric: t("Scope 2 Emissions (t CO₂e)"),
          baseYear: s2Base.toFixed(2),
          mry: s2MRY.toFixed(2),
          targetYear: s2Target.toFixed(2),
          reductionToDate: s2Red,
          sbtReduction: `${sbtRedVal}%`,
        },
        {
          id: "total",
          metric: t("Scope 1 + 2 Emissions (t CO₂e)"),
          baseYear: totalBase.toFixed(2),
          mry: totalMRY.toFixed(2),
          targetYear: totalTarget.toFixed(2),
          reductionToDate: totalRed,
          sbtReduction: `${sbtRedVal}%`,
          isTotal: true,
        },
      ];
    }, [scope1Emission, scope2Emission, MRYScope1Emission, MRYScope2Emission, scope1TargetEmission, scope2TargetEmission, yearsList, t]);

    const acaColDefs = useMemo(() => [
      {
        headerName: t("Metric"),
        field: "metric",
        ...leftAlignColDef,
        flex: 1.8,
        minWidth: 220,
        cellStyle: (params) => ({
          ...leftAlignColDef.cellStyle,
          fontWeight: params.data?.isTotal ? 700 : 500,
        }),
      },
      {
        headerName: `${t("Base Year")} (${baseYear || "—"})`,
        field: "baseYear",
        ...centerAlignColDef,
        flex: 1,
        minWidth: 120,
        cellStyle: (params) => ({
          ...centerAlignColDef.cellStyle,
          ...(params.data?.isTotal ? { fontWeight: 700 } : {}),
        }),
      },
      {
        headerName: `${t("Most Recent Year")} (${mostRecentYear || "—"})`,
        field: "mry",
        ...centerAlignColDef,
        flex: 1.1,
        minWidth: 130,
        cellStyle: (params) => ({
          ...centerAlignColDef.cellStyle,
          ...(params.data?.isTotal ? { fontWeight: 700 } : {}),
        }),
      },
      {
        headerName: `${t("Target Year")} (${targetYear || "—"})`,
        field: "targetYear",
        ...centerAlignColDef,
        flex: 1,
        minWidth: 120,
        cellStyle: (params) => ({
          ...centerAlignColDef.cellStyle,
          ...(params.data?.isTotal ? { fontWeight: 700 } : {}),
        }),
      },
      {
        headerName: t("% Reduction to Date"),
        field: "reductionToDate",
        ...rightAlignColDef,
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (params) => {
          if (params.value === undefined || params.value === null || params.value === "—") return "—";
          if (params.value === "N/A") {
            return (
              <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", alignItems: "center" }}>
                <Chip tone="neutral" size="sm">
                  {t("N/A")}
                </Chip>
              </Box>
            );
          }
          const numVal = parseFloat(params.value);
          if (isNaN(numVal)) return "—";
          const tone = numVal > 0 ? "success" : numVal < 0 ? "error" : "neutral";
          const formattedText = `${numVal > 0 ? "+" : ""}${numVal.toFixed(2)}%`;
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", alignItems: "center" }}>
              <Chip tone={tone} size="sm">
                {formattedText}
              </Chip>
            </Box>
          );
        },
      },
      {
        headerName: t("% SBT Reduction"),
        field: "sbtReduction",
        ...rightAlignColDef,
        flex: 1,
        minWidth: 130,
        cellStyle: (params) => ({
          ...rightAlignColDef.cellStyle,
          fontWeight: params.data?.isTotal ? 700 : 600,
        }),
      },
    ], [baseYear, mostRecentYear, targetYear, t]);

    return (
       <>

      <FormSection
        title={t("Input Data")}
        action={
          <M3Button variant="outlined" size="sm" onClick={resetForm}>
            {t("Reset")}
          </M3Button>
        }
        className="export-ignore"
      >
        <FieldGrid columns={4}>
          <M3Field
            label={t("Target Setting Method")}
            value="Absolute Contraction Approach"
            disabled
          />
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--m3-on-surface-variant, #454840)", marginBottom: "6px" }}>
              {t("Base Year")}
            </label>
            <select
              className="action-year-select"
              style={selectStyle}
              value={baseYear}
              onChange={(e) => { setBaseYear(e.target.value); setIsSaveVisible(true); }}
            >
              <option value="">{t("Select Year")}</option>
              {baseYearList.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--m3-on-surface-variant, #454840)", marginBottom: "6px" }}>
              {t("Most Recent Year")}
            </label>
            <select
              className="action-year-select"
              style={selectStyle}
              value={mostRecentYear}
              onChange={(e) => { setMostRecentYear(e.target.value); setIsSaveVisible(true); }}
            >
              <option value="">{t("Select Year")}</option>
              {mostRecentYearList.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--m3-on-surface-variant, #454840)", marginBottom: "6px" }}>
              {t("Target Year")}
            </label>
            <select
              className="action-year-select"
              style={selectStyle}
              value={targetYear}
              onChange={(e) => { setTargetYear(e.target.value); setIsSaveVisible(true); }}
            >
              <option value="">{t("Select Year")}</option>
              {targetYearList.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <M3Field
            label={t("Base Year | Scope 1")}
            value={Number(scope1Emission || 0).toFixed(2)}
            suffix="tCO₂e"
            disabled
          />
          <M3Field
            label={t("Base Year | Scope 2")}
            value={Number(scope2Emission || 0).toFixed(2)}
            suffix="tCO₂e"
            disabled
          />
          <M3Field
            label={t("MRY | Scope 1")}
            value={Number(MRYScope1Emission || 0).toFixed(2)}
            suffix="tCO₂e"
            disabled
          />
          <M3Field
            label={t("MRY | Scope 2")}
            value={Number(MRYScope2Emission || 0).toFixed(2)}
            suffix="tCO₂e"
            disabled
          />
        </FieldGrid>
      </FormSection>

      <Box sx={{ mt: 3 }} className="export-ignore">
        <DataTableCard title={t("Cross-Sector Absolute Reduction (ACA)")}>
          <AgGrid
            columnDefs={acaColDefs}
            rowData={acaRowData}
            domLayout="autoHeight"
            headerCheckbox={false}
            checkboxes={false}
            pagination={false}
            suppressPagination={true}
          />
        </DataTableCard>
      </Box>

        <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
            <ChartCard
              title={t("Short Term Emissions")}
              actions={
                <SegmentedButton
                  options={[
                    { value: "logarithmic", label: t("Log Scale") },
                    { value: "linear", label: t("Linear") },
                  ]}
                  value={yScaleType}
                  onChange={setYScaleType}
                />
              }
            >
                {yearsList && yearsList.length > 0 ? (
                  <div style={{ width: "100%", height: 450, position: "relative" }}>
                      <Line data={shortTermGraphData} options={shortTermGraphOptions} />
                  </div>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 280, gap: 1 }}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {t("Please select Base Year and Target Year above to view target pathways.")}
                    </Typography>
                  </Box>
                )}
            </ChartCard>
        </Grid>
      </>
    );
}

export default Scope12ShortTermTool;